/**
 * Permission Constants
 * Module names, permission levels, and actions for the application
 *
 * All permissions are resolved from roles (user_role_assignments + role_permissions).
 */

// Permission Levels (used by ProtectedRoute and Sidebar for route definitions)
export const PERMISSION_LEVELS = {
  NONE: 'none',
  READ: 'read',
  WRITE: 'write',
  ADMIN: 'admin',
};

// Granular Actions (role-based system)
export const ACTIONS = {
  READ: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  APPROVE: 'approve',
};

// Action labels for UI display
export const ACTION_LABELS = {
  read: 'Read',
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
  approve: 'Approve',
};

// Modules
export const MODULES = {
  CAPITAL_MANAGEMENT: 'capital_management',
  CONTRACT_MANAGEMENT: 'contract_management',
  STAKEHOLDER_MANAGEMENT: 'stakeholder_management',
  REFERENCE_DATA: 'reference_data',
  AMORTIZATION_SCHEDULES: 'amortization_schedules',
  REPORTS: 'reports',
  ADMIN_SETTINGS: 'admin_settings',
  USER_MANAGEMENT: 'user_management',
  TENANT_MANAGEMENT: 'tenant_management',
  PAYMENTS: 'payments',
  AUDIT_TRAIL: 'audit_trail',
  CLIENT_PORTAL: 'client_portal',
  WORKFLOW_DOCUMENT_REQUIREMENTS: 'workflow_document_requirements',
  NOTES: 'notes',
};

// Module labels for UI display
export const MODULE_LABELS = {
  capital_management: 'Capital Management',
  contract_management: 'Contract Management',
  stakeholder_management: 'Stakeholders',
  reference_data: 'Reference Data',
  amortization_schedules: 'Amortization',
  reports: 'Reports',
  admin_settings: 'Admin Settings',
  user_management: 'User Management',
  tenant_management: 'Tenant Management',
  payments: 'Payments',
  audit_trail: 'Audit Trail',
  client_portal: 'Client Portal',
  workflow_document_requirements: 'Workflow Document Requirements',
  notes: 'Notes',
};

// Module categories for grouping in UI
export const MODULE_CATEGORIES = {
  business: [
    'capital_management',
    'contract_management',
    'payments',
    'stakeholder_management',
    'reports',
    'amortization_schedules',
    'notes',
  ],
  admin: [
    'reference_data',
    'admin_settings',
    'user_management',
    'tenant_management',
    'audit_trail',
    'client_portal',
    'workflow_document_requirements',
  ],
};

/**
 * Check if user has a specific action on a module
 * @param {Object} permissions - Action-based permissions { module: { action: bool } }
 * @param {string} module - Module name
 * @param {string} action - Required action (read, create, update, delete, approve)
 * @returns {boolean} Has the action
 */
export const hasAction = (permissions, module, action) => {
  if (!permissions || !module || !action) return false;

  const modulePerms = permissions[module];

  if (modulePerms && typeof modulePerms === 'object') {
    return modulePerms[action] === true;
  }

  return false;
};

/**
 * Module Tree Structure
 * 3-level hierarchy: Module → Category → Feature
 * Used by the tenant module configuration tree UI.
 * Adding/removing nodes here automatically updates the UI.
 */
// MODULE_TREE is the source of truth for the role permission matrix AND the per-tenant
// module selector. Both iterate this tree and intersect it with tenant_modules.config.
//
// Sub-module (child) entries below correspond to REAL feature-flag keys the app gates on via
// isFeatureEnabled('<module>', '<key>'). Only four modules actually read sub-feature flags:
//   - stakeholder_management (StakeholderManagement.js + StakeholderFormDialog.js)
//   - reports (ReportsPage.js allReportTabs)
//   - reference_data (ReferenceDataManagement.js allRefTabs)
//   - payments (PaymentMatching.js allPaymentTabs)
// Every other module is a FLAT node: permission enforcement is top-level only (role_permissions
// stores module_code, never sub-modules), and no isFeatureEnabled / requireModuleFeature call
// references their sub-keys. Declaring phantom children there made the role matrix show
// checkboxes that enforce nothing — they were pruned during the 2026-06-25 RBAC reconciliation.
// (Per-feature enforcement via requireModuleFeature is a separate, deliberate future effort.)
export const MODULE_TREE = {
  capital_management: {
    label: 'Capital Management',
  },
  contract_management: {
    label: 'Contract Management',
  },
  // Notes: gross/net-rate notes with a commission spread. Flat node (no sub-feature flags);
  // off by default per tenant (M3-025), enabled in Tenant Management.
  notes: {
    label: 'Notes',
  },
  payments: {
    label: 'Payments',
    children: {
      matching: { label: 'Payment Matching' },
      capital_receipts: { label: 'Capital Receipts' },
      withholding_tax: { label: 'Withholding Tax' },
    },
  },
  stakeholder_management: {
    label: 'Stakeholders',
    children: {
      investors: {
        label: 'Investor Management',
        children: {
          basic_information: { label: 'Basic Information' },
          identity_classification: { label: 'Identity & Classification' },
          banking_details: { label: 'Banking Details' },
          contact_details: { label: 'Contact Details' },
          kyc_aml: { label: 'KYC / AML Compliance' },
          relationship_management: { label: 'Relationship Management' },
          wht_override: { label: 'WHT Override' },
          commission_override: { label: 'Commission Override' },
        },
      },
      brokers: {
        label: 'Broker Management',
        children: {
          basic_information: { label: 'Basic Information' },
          contact_info: { label: 'Contact Information' },
          commission_structure: { label: 'Commission Structure' },
          regulatory_licensing: { label: 'Regulatory & Licensing' },
          relationship: { label: 'Relationship' },
        },
      },
      collateral: {
        label: 'Collateral Providers',
        children: {
          basic_information: { label: 'Basic Information' },
          contact_info: { label: 'Contact Information' },
          credit_capacity: { label: 'Credit & Capacity' },
          regulatory: { label: 'Regulatory' },
          capability: { label: 'Capability' },
        },
      },
      issuers: {
        label: 'Issuer Management',
        children: {
          basic_information: { label: 'Basic Information' },
        },
      },
    },
  },
  reports: {
    label: 'Reports',
    children: {
      contract_register: { label: 'Contract Register' },
      payment_schedule: { label: 'Payment Schedule' },
      investor_summary: { label: 'Investor Summary' },
      current_long_term: { label: 'Current vs Long-Term' },
      rolled_contracts: { label: 'Rolled Contracts' },
      rollover_fund_flow: { label: 'Rollover Fund Flow' },
      contract_suspense: { label: 'Rollover Suspense' },
      interest_accrual_journal: { label: 'Interest Accrual Journal' },
      issuer_ledger: { label: 'Sub Ledger' },
      audit_trail: { label: 'Audit Trail' },
    },
  },
  reference_data: {
    label: 'Reference Data',
    children: {
      countries: { label: 'Countries & Currencies' },
      frequencies: { label: 'Frequencies' },
      investment_types: { label: 'Investment Types' },
      programs: { label: 'Programs' },
      interest_rates: { label: 'Interest Rates' },
      fx_rates: { label: 'FX Rates' },
      placement_fees: { label: 'Placement Fees' },
      collateral_types: { label: 'Collateral Types' },
      narrations: { label: 'Narrations' },
      wht_rules: { label: 'WHT Rules' },
      commission_rates: { label: 'Commission Rates' },
      calculation_profiles: { label: 'Calculation Profiles' },
    },
  },
  user_management: {
    label: 'User Management',
  },
  admin_settings: {
    label: 'Admin Settings',
  },
  tenant_management: {
    label: 'Tenant Management',
  },
  amortization_schedules: {
    label: 'Amortization Schedules',
  },
  client_portal: {
    label: 'Client Portal',
  },
  audit_trail: {
    label: 'Audit Trail',
  },
  workflow_document_requirements: {
    label: 'Workflow Document Requirements',
  },
};

/**
 * Get all modules as an array (for iteration)
 */
export const getAllModules = () => Object.values(MODULES);

/**
 * Get all actions as an array (for iteration)
 */
export const getAllActions = () => Object.values(ACTIONS);
